/**
 * DemoBookCallModal — B2B lead-capture form for the public demo audience.
 *
 * Opens from ``DemoBookCallBanner`` (or any other "Prenota demo" affordance
 * that flips ``useDemoBookCallStore.openModal``). Submits to
 * ``POST /demo/book-call-lead`` which the BE forwards to Instantly + the
 * sales Slack channel; on success we swap the card to a "grazie" state and
 * — if the BE returned one — offer an immediate Calendly deep-link.
 *
 * Built on the same headless primitives as ``DemoWelcomeModal``: a portal +
 * scrim + Framer Motion card, minimal focus trap, ``Esc`` to close. No
 * generic modal lib.
 *
 * Client-side email validation rejects consumer providers (@gmail.com,
 * @outlook, @libero.it, …) with a friendly inline error so the sales team
 * doesn't drown in noise — the sales team's ICP is B2B business emails.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";

import { cn, useReducedMotion } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ArrowRight, Loader2, Sparkles, X } from "lucide-react";

import {
  type BookCallLeadResponse,
  submitDemoBookCallLead,
} from "@ando/core/api";

import { useActiveConnectorsCount } from "@/features/capabilities";
import { useChatStore } from "@/features/chat";
import { useDocGenerationStore } from "@/features/sources/state/docGenerationStore";

import { useDemoBookCallStore } from "./demoBookCallStore";
import { useOnboardingMode } from "./useOnboardingMode";

/**
 * Consumer / freemail domains rejected client-side. Case-insensitive;
 * we compare against the LOWERCASED domain (including all subdomains
 * matching ``*.hotmail.*`` etc.).
 */
const CONSUMER_DOMAINS: readonly string[] = [
  "gmail.com",
  "yahoo.com",
  "yahoo.it",
  "yahoo.co.uk",
  "yahoo.fr",
  "yahoo.es",
  "hotmail.com",
  "hotmail.it",
  "hotmail.fr",
  "hotmail.es",
  "outlook.com",
  "outlook.it",
  "outlook.fr",
  "outlook.es",
  "live.com",
  "live.it",
  "live.fr",
  "icloud.com",
  "me.com",
  "mac.com",
  "libero.it",
  "alice.it",
  "virgilio.it",
  "tin.it",
];

function isConsumerEmail(email: string): boolean {
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain) return false;
  return CONSUMER_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`));
}

/** RFC-5322-lite: enough to guard against typos without a full parser. */
function looksLikeEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function DemoBookCallModal(): React.ReactElement | null {
  const { i18n } = useLingui();
  const reducedMotion = useReducedMotion();
  const mode = useOnboardingMode();
  const isDemo = mode === "demo";

  const { modalOpen, closeModal, setCaptured } = useDemoBookCallStore(
    useShallow((s) => ({
      modalOpen: s.modalOpen,
      closeModal: s.closeModal,
      setCaptured: s.setCaptured,
    })),
  );
  const triggersFired = useDemoBookCallStore((s) => s.triggersFired);

  // Session-metadata inputs — read at render time so the payload reflects the
  // latest engagement state each submit attempt.
  const totalUserMessages = useChatStore((s) => {
    let n = 0;
    for (const list of Object.values(s.messagesByConversation)) {
      for (const m of list) {
        if (m.role === "user") n += 1;
      }
    }
    return n;
  });
  const connectorCount = useActiveConnectorsCount();
  const docGenerationEverStarted = useDocGenerationStore((s) => s.active);

  // Mount timestamp — best available proxy for "session start" in the FE
  // (no server identity for demo visitors). Frozen for the modal's lifetime.
  const sessionStartRef = useRef<number>(Date.now());

  // ── Form state ────────────────────────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<BookCallLeadResponse | null>(null);

  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);

  // Reset transient state whenever the modal is closed (not when it opens —
  // the user might reopen after correcting an error and expect their inputs
  // to survive; but a clean close is a full reset).
  useEffect(() => {
    if (modalOpen) return;
    setEmail("");
    setFullName("");
    setCompanyName("");
    setPhone("");
    setMessage("");
    setEmailError(null);
    setSubmitError(null);
    setSuccess(null);
    setSubmitting(false);
  }, [modalOpen]);

  const close = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // Focus the first input on open.
  useLayoutEffect(() => {
    if (!cardEl) return;
    const first = cardEl.querySelector<HTMLElement>("[data-book-call-first]");
    first?.focus();
  }, [cardEl]);

  // Esc closes — but never mid-submit (avoid losing form state on accidental keypress).
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !submitting) {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, submitting, close]);

  const validateEmail = useCallback(
    (raw: string): string | null => {
      if (!raw.trim()) return i18n._(msg`Inserisci un'email.`);
      if (!looksLikeEmail(raw)) return i18n._(msg`Email non valida.`);
      if (isConsumerEmail(raw)) return i18n._(msg`Serve un'email aziendale — grazie!`);
      return null;
    },
    [i18n],
  );

  const onEmailBlur = useCallback(() => {
    if (!email) return;
    setEmailError(validateEmail(email));
  }, [email, validateEmail]);

  const onEmailChange = useCallback(
    (v: string) => {
      setEmail(v);
      // Clear the error as soon as the user starts typing again.
      if (emailError) setEmailError(null);
    },
    [emailError],
  );

  const sourceUtm = useMemo<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const params = new URLSearchParams(window.location.search);
      const utm =
        params.get("utm_source") ??
        params.get("utm_campaign") ??
        params.get("utm_medium") ??
        null;
      if (utm) return utm;
      return document.referrer || null;
    } catch {
      return null;
    }
  }, []);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submitting) return;
      const err = validateEmail(email);
      setEmailError(err);
      if (err) return;
      if (!fullName.trim() || !companyName.trim()) return;

      setSubmitting(true);
      setSubmitError(null);
      try {
        const uptimeMin = Math.max(
          0,
          Math.round((Date.now() - sessionStartRef.current) / 60_000),
        );
        // Assemble the loose feature-touch list from state signals we already
        // subscribe to — no new instrumentation required.
        const featuresTouched: string[] = [];
        if (totalUserMessages > 0) featuresTouched.push("chat");
        if (docGenerationEverStarted) featuresTouched.push("document_authoring");
        if (connectorCount > 0) featuresTouched.push("connectors");
        const response = await submitDemoBookCallLead({
          email: email.trim(),
          full_name: fullName.trim(),
          company_name: companyName.trim(),
          phone: phone.trim() || undefined,
          message: message.trim() || undefined,
          session_metadata: {
            uptime_min: uptimeMin,
            queries_done: totalUserMessages,
            first_wow_moment: triggersFired[0] ?? null,
            features_touched: featuresTouched,
            language: i18n.locale,
            source_utm: sourceUtm,
          },
        });
        setSuccess(response);
      } catch (err_) {
        setSubmitError(
          i18n._(msg`Non siamo riusciti a inviare la richiesta. Riprova tra un attimo.`),
        );
        // Keep the form state — the user hits "Riprova" without retyping. Log
        // via ``console.error`` so support can correlate; safe (no PII in the
        // error object; the email address never lands in the message string).
        console.error("[book-call-lead] submit failed", err_);
      } finally {
        setSubmitting(false);
      }
    },
    [
      companyName,
      connectorCount,
      docGenerationEverStarted,
      email,
      fullName,
      i18n,
      message,
      phone,
      sourceUtm,
      submitting,
      totalUserMessages,
      triggersFired,
      validateEmail,
    ],
  );

  const onCloseAfterSuccess = useCallback(() => {
    setCaptured(true);
    close();
  }, [close, setCaptured]);

  if (!isDemo || !modalOpen) return null;

  const ease = [0.32, 0.72, 0, 1] as const;

  // Minimal focus trap: keep Tab cycling within the card.
  const onCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== "Tab" || !cardEl) return;
    const focusables = cardEl.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[135] flex items-center justify-center p-4">
      <motion.div
        aria-hidden
        onClick={submitting ? undefined : close}
        className="absolute inset-0 bg-ando-ink/45"
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.2 }}
      />

      <AnimatePresence>
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-book-call-title"
          ref={setCardEl}
          onKeyDown={onCardKeyDown}
          initial={reducedMotion ? false : { opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: reducedMotion ? 0 : 0.28, ease }}
          className={cn(
            "relative z-10 w-full max-w-[480px] overflow-hidden rounded-2xl border border-hairline bg-card",
            "shadow-[0_0_0_1px_rgba(60,40,20,0.05),0_24px_56px_-24px_rgba(60,40,20,0.35)]",
          )}
        >
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            aria-label={i18n._(msg`Chiudi`)}
            className="absolute top-4 right-4 z-10 grid h-9 w-9 place-items-center rounded-full text-ink-3 transition-colors hover:bg-ando-ink/[0.06] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={16} strokeWidth={1.75} aria-hidden />
          </button>

          {success ? (
            <SuccessState
              response={success}
              onClose={onCloseAfterSuccess}
            />
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-4 px-7 pt-8 pb-7" noValidate>
              <span
                aria-hidden
                className="grid h-10 w-10 place-items-center rounded-xl border border-ando-orange/40 bg-transparent text-ando-orange"
              >
                <Sparkles size={20} strokeWidth={1.9} />
              </span>

              <div className="flex flex-col gap-1">
                <h2
                  id="demo-book-call-title"
                  className="font-semibold text-[20px] text-ink-1 leading-tight tracking-tight"
                >
                  <Trans>Prenota 15 min col team</Trans>
                </h2>
                <p className="text-[13.5px] text-ink-2 leading-relaxed">
                  <Trans>
                    Ti mostriamo Ando sui tuoi dati reali. Lascia due dettagli e ti
                    scriviamo entro il giorno.
                  </Trans>
                </p>
              </div>

              <Field label={i18n._(msg`Email aziendale`)} required>
                <input
                  data-book-call-first
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  onBlur={onEmailBlur}
                  placeholder="nome@azienda.com"
                  aria-invalid={emailError ? "true" : "false"}
                  aria-describedby={emailError ? "book-call-email-error" : undefined}
                  className={cn(
                    "w-full rounded-md border bg-card px-3 py-2 text-[14px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                    emailError ? "border-red-400" : "border-hairline",
                  )}
                />
                {emailError ? (
                  <span
                    id="book-call-email-error"
                    className="mt-1 inline-flex items-center gap-1 text-[12px] text-red-600"
                  >
                    <AlertCircle size={12} strokeWidth={2} aria-hidden />
                    {emailError}
                  </span>
                ) : null}
              </Field>

              <Field label={i18n._(msg`Nome e cognome`)} required>
                <input
                  type="text"
                  autoComplete="name"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={i18n._(msg`Mario Rossi`)}
                  className="w-full rounded-md border border-hairline bg-card px-3 py-2 text-[14px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
                />
              </Field>

              <Field label={i18n._(msg`Azienda`)} required>
                <input
                  type="text"
                  autoComplete="organization"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={i18n._(msg`La tua azienda S.r.l.`)}
                  className="w-full rounded-md border border-hairline bg-card px-3 py-2 text-[14px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
                />
              </Field>

              <Field label={i18n._(msg`Telefono (opzionale)`)}>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+39 …"
                  className="w-full rounded-md border border-hairline bg-card px-3 py-2 text-[14px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
                />
              </Field>

              <Field label={i18n._(msg`Messaggio (opzionale)`)}>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={i18n._(msg`Cosa vorresti vedere?`)}
                  rows={3}
                  className="w-full resize-none rounded-md border border-hairline bg-card px-3 py-2 text-[14px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
                />
              </Field>

              {submitError ? (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700"
                >
                  <AlertCircle
                    size={14}
                    strokeWidth={2}
                    aria-hidden
                    className="mt-0.5 shrink-0"
                  />
                  <span>{submitError}</span>
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="mt-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-ando-orange px-5 font-semibold text-[14px] text-white transition-all hover:bg-ando-orange-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2
                      size={16}
                      strokeWidth={2}
                      aria-hidden
                      className="animate-spin"
                    />
                    <Trans>Invio…</Trans>
                  </>
                ) : submitError ? (
                  <Trans>Riprova</Trans>
                ) : (
                  <>
                    <Trans>Prenota</Trans>
                    <ArrowRight size={16} strokeWidth={2} aria-hidden />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body,
  );
}

DemoBookCallModal.displayName = "DemoBookCallModal";

// ─── Local subcomponents ───────────────────────────────────────────────────

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, required, children }: FieldProps): React.ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] text-ink-2">
        {label}
        {required ? <span className="ml-0.5 text-ando-orange">*</span> : null}
      </span>
      {children}
    </label>
  );
}

interface SuccessStateProps {
  response: BookCallLeadResponse;
  onClose: () => void;
}

function SuccessState({ response, onClose }: SuccessStateProps): React.ReactElement {
  const { i18n } = useLingui();
  const hasCalendly = Boolean(response.calendly_url);

  return (
    <div className="flex flex-col items-start gap-4 px-7 pt-8 pb-7">
      <span
        aria-hidden
        className="grid h-10 w-10 place-items-center rounded-xl border border-ando-orange/40 bg-transparent text-ando-orange"
      >
        <Sparkles size={20} strokeWidth={1.9} />
      </span>

      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-[20px] text-ink-1 leading-tight tracking-tight">
          <Trans>Grazie!</Trans>
        </h2>
        <p className="text-[13.5px] text-ink-2 leading-relaxed">
          <Trans>Ti abbiamo aggiunto in coda. Vuoi prenotare direttamente ora?</Trans>
        </p>
      </div>

      {hasCalendly ? (
        <button
          type="button"
          onClick={() => {
            const url = response.calendly_url;
            if (url) window.open(url, "_blank", "noopener,noreferrer");
          }}
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-ando-orange px-5 font-semibold text-[14px] text-white transition-all hover:bg-ando-orange-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        >
          <Trans>Prenota subito</Trans>
          <ArrowRight size={16} strokeWidth={2} aria-hidden />
        </button>
      ) : null}

      <button
        type="button"
        onClick={onClose}
        aria-label={i18n._(msg`Torna alla demo`)}
        className={cn(
          "inline-flex h-10 w-full items-center justify-center rounded-xl border border-hairline bg-transparent px-5 font-medium text-[13.5px] text-ink-1 transition-colors hover:bg-ando-ink/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
        )}
      >
        <Trans>Torna alla demo</Trans>
      </button>
    </div>
  );
}
